import { Box, type BoxProps } from '@chakra-ui/react'
import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useId,
    useMemo,
    useRef,
    useState,
    type Dispatch,
    type ReactNode,
    type SetStateAction
} from 'react'
import { createPortal } from 'react-dom'
import { SidebarItem, type SidebarItemRender } from './SidebarItem'

// Context 1: Where should the content go? (Read-only for items)
const TargetContext = createContext<HTMLElement | null>(null)

// Context 2: How do we register the root sidebar? (Write-only for the Outlet)
const RegistryContext = createContext<Dispatch<SetStateAction<HTMLElement | null>>>(() => { })

// Context 3: How do we render the sidebar items?
const SidebarItemRenderContext = createContext<SidebarItemRender>(SidebarItem)

const NumberingContext = createContext<{
    register: (id: string) => void;
    unregister: (id: string) => void;
    ids: string[];
    prefix: string;
}>({
    register: () => { },
    unregister: () => { },
    ids: [],
    prefix: ''
})

// --- 1. The Provider (Wrap your App or RootLayout) ---
export const SidebarProvider = ({ children }: { children: ReactNode }) => {
    const [rootNode, setRootNode] = useState<HTMLElement | null>(null)
    const [childIds, setChildIds] = useState<string[]>([])

    const register = useCallback((id: string) => {
        setChildIds(prev => {
            if (prev.includes(id)) return prev // Safety check
            return [...prev, id]
        })
    }, [])

    const unregister = useCallback((id: string) => {
        setChildIds(prev => prev.filter(x => x !== id))
    }, [])

    const numberingValue = useMemo(() => ({
        ids: childIds,
        prefix: '',
        register,
        unregister
    }), [childIds, register, unregister])

    return (
        <SidebarItemRenderContext.Provider value={SidebarItem}>
            <RegistryContext.Provider value={setRootNode}>
                <TargetContext.Provider value={rootNode}>
                    <NumberingContext.Provider value={numberingValue}>
                        {children}
                    </NumberingContext.Provider>
                </TargetContext.Provider>
            </RegistryContext.Provider>
        </SidebarItemRenderContext.Provider>
    )
}

// --- 2. The Outlet (Place this wherever you want the sidebar to appear) ---
export const SidebarOutlet = (props: BoxProps) => {
    const setRootNode = useContext(RegistryContext)

    return <Box as='ul' ref={setRootNode} listStyleType='none' py={8} {...props} />
}

interface SidebarItemProps {
    label: string;
    children: ReactNode;
    isNumbered?: boolean;
}

export const SidebarEntry = ({ label, children, isNumbered = true }: SidebarItemProps) => {
    const parentTarget = useContext(TargetContext)
    const sidebarItemRender = useContext(SidebarItemRenderContext)

    const [childTarget, setChildTarget] = useState<HTMLElement | null>(null)
    const contentRef = useRef<HTMLDivElement>(null)

    const scrollToContent = useCallback((e: React.BaseSyntheticEvent) => {
        e.stopPropagation()
        if (contentRef.current === null) return
        const y = contentRef.current.getBoundingClientRect().top + window.scrollY - 20
        window.scrollTo({ top: y, behavior: 'smooth' })
    }, [])

    const myNumberString = useSidebarNumbering(isNumbered)

    const [myChildIds, setMyChildIds] = useState<string[]>([])

    const register = useCallback((id: string) => {
        setMyChildIds(prev => {
            if (prev.includes(id)) return prev
            return [...prev, id]
        })
    }, [])

    const unregister = useCallback((id: string) => {
        setMyChildIds(prev => prev.filter(x => x !== id))
    }, [])

    const childNumberingValue = useMemo(() => ({
        ids: myChildIds,
        prefix: myNumberString || '',
        register,
        unregister
    }), [myChildIds, myNumberString, register, unregister])

    const [isActive, setIsActive] = useState(false)
    useEffect(() => {
        const handleScroll = () => {
            if (!contentRef.current) return

            const { offsetTop, offsetHeight } = contentRef.current
            const scrollY = window.scrollY

            // We add a -40px buffer so it activates slightly before reaching the exact top
            const offset = 40
            const isVisible = scrollY >= (offsetTop - offset) &&
                scrollY < (offsetTop + offsetHeight - offset)

            // Only trigger re-render if value actually changes
            setIsActive(prev => prev !== isVisible ? isVisible : prev)
        }

        window.addEventListener('scroll', handleScroll, { passive: true })
        handleScroll() // Check initial position on mount

        return () => window.removeEventListener('scroll', handleScroll)
    }, [])

    const sidebarNode = sidebarItemRender({
        number: myNumberString,
        level: 0,
        label,
        onClick: (e) => scrollToContent(e),
        setChildTarget,
        isActive: isActive
    })

    return (
        <TargetContext.Provider value={childTarget}>
            <NumberingContext.Provider value={childNumberingValue}>
                {parentTarget && createPortal(sidebarNode, parentTarget)}
                <Box ref={contentRef} position='relative'>
                    {children}
                </Box>
            </NumberingContext.Provider>
        </TargetContext.Provider>
    )
}


/**
 * Manages the registration of a sidebar item to determine its index.
 * Returns the full numbering string (e.g. "1.2")
 */
const useSidebarNumbering = (isNumbered: boolean) => {
    const { register, unregister, ids, prefix } = useContext(NumberingContext)

    const id = useId()

    // 1. Register with parent context on mount
    useEffect(() => {
        if (!isNumbered) return
        register(id)
        return () => unregister(id)
    }, [register, unregister, id, isNumbered])

    // 2. Calculate "My Index" based on position in parent's ID list
    const index = ids.indexOf(id) + 1

    // 3. Format the string (ParentPrefix + MyIndex)
    if (index === 0) return undefined // Not registered yet or not found
    return prefix ? `${prefix}${index}.` : `${index}.`
}